/**
 * Role semantics of the company network graph (GET /companies/{id}/network),
 * shared by the XyFlow diagram (company-detail/CompanyNetworkDiagram) and the
 * company page's network view (company-network/). Pure functions, client-safe.
 */
import type {
  CompanyNetworkResponse,
  NetworkEdge,
  NetworkNode,
} from '@/lib/backend-api';

// ---------------------------------------------------------------------------
// Edge direction normalisation
// Backend convention: source = role-bearer (owner, manager, parent), target =
// subject (the company the role is in). In ELK's DOWN layout the source ends
// up ABOVE the target, so the natural direction already places owners and
// managers above their companies and subsidiaries below the focal that owns
// them. The only fix we apply is when the backend hands us a person on the
// target side of an upstream/downstream role: swap so the person sits above.
// ---------------------------------------------------------------------------
export const UPSTREAM_ROLES = [
  'gesellschafter',
  'kommanditist',
  'aktionär',
  'aktionaer',
  'anteilseigner',
  'inhaber',
  'shareholder',
  'alleingesellschafter',
  'mehrheitsgesellschafter',
  'minderheitsgesellschafter',
  'stiller gesellschafter',
  'beteiligung',
  'muttergesellschaft',
  'muttergesellschaft',
  'holding',
  'konzernmutter',
  'parent',
  'parent company',
];

export const DOWNSTREAM_ROLES = [
  'vorstand',
  'geschäftsführer',
  'geschaeftsfuehrer',
  'prokurist',
  'prokura',
  'direktor',
  'aufsichtsrat',
  'beirat',
  'liquidator',
  'abwickler',
  'geschäftsführerin',
  'vorstandsvorsitzender',
  'generaldirektor',
  'ceo',
  'cfo',
  'cto',
  'coo',
];

// Roles where the backend uses an INVERTED convention: source = the
// subordinate entity (subsidiary), target = the parent. Edges like these
// arrive with the subsidiary as source, so in ELK's DOWN layout the
// subsidiary would land above the parent. We flip them so the parent
// (target) ends up above the subsidiary.
export const SUBSIDIARY_ROLES = [
  'tochter',
  'tochtergesellschaft',
  'tochterunternehmen',
  'subsidiary',
  'subsidiary company',
  'zweigniederlassung',
];

export function normaliseEdges(
  edges: NetworkEdge[],
  nodeMap: Map<string, NetworkNode>,
): NetworkEdge[] {
  return edges.map((e) => {
    const role = (e.role ?? '').toLowerCase();
    const isUpstream = isUpstreamRole(role);
    const isDownstream = isManagingRole(role);
    const isSubsidiary = SUBSIDIARY_ROLES.some((r) => role.includes(r));

    // Subsidiary roles arrive as (subsidiary, parent) — flip so the parent
    // is the source and the subsidiary sits below it in ELK's DOWN layout.
    if (isSubsidiary) {
      return { ...e, source: e.target, target: e.source };
    }

    if (!isUpstream && !isDownstream) return e;

    // Defensive: backend almost always returns (role-bearer, company). When
    // it doesn't — i.e., a person sits on the target side of an owner/manager
    // role — swap so the person ends up as source (above the company).
    const srcType = nodeMap.get(e.source)?.node_type;
    const tgtType = nodeMap.get(e.target)?.node_type;
    if (srcType === 'company' && tgtType === 'person') {
      return { ...e, source: e.target, target: e.source };
    }
    return e;
  });
}

export function isSubsidiaryRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  return SUBSIDIARY_ROLES.some((x) => r.includes(x));
}

// "Persönlich haftende(r) Gesellschafter(in)" / Komplementär is the general
// partner of a KG — typically a liability-shield entity that MANAGES the company
// without a real ownership stake. It contains the word "Gesellschafter" but must
// be classified as a managing role, never as ownership.
export function isManagingRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  return (
    DOWNSTREAM_ROLES.some((x) => r.includes(x)) ||
    r.includes('persönlich haftend') ||
    r.includes('persoenlich haftend') ||
    r.includes('komplement')
  );
}

export function isUpstreamRole(role?: string | null): boolean {
  const r = (role ?? '').toLowerCase();
  if (isManagingRole(r)) return false; // Komplementär / persönlich haftend
  return UPSTREAM_ROLES.some((x) => r.includes(x));
}

/**
 * Merge company nodes that share the exact same label (company name) into a
 * single node. The backend's multi-level upstream stitching can surface the
 * same company both resolved (id = eu_id) and unresolved (id = canonical_key,
 * node_type "company_entity"), producing visual duplicates. The survivor is the
 * focal, else the node carrying the most identity (eu_id + register info); its
 * missing identity fields are backfilled from the duplicates, all edges are
 * redirected to it, self-loops dropped, and parallel edges de-duplicated.
 * Persons are matched by canonical_key upstream, so only companies merge here.
 */
export function mergeSameNameNodes(
  data: CompanyNetworkResponse,
): CompanyNetworkResponse {
  const byLabel = new Map<string, NetworkNode[]>();
  for (const n of data.nodes) {
    if (n.node_type === 'person') continue;
    const key = (n.label ?? '').trim();
    if (!key) continue;
    if (!byLabel.has(key)) byLabel.set(key, []);
    byLabel.get(key)!.push(n);
  }
  const remap = new Map<string, string>();
  const enriched = new Map<string, NetworkNode>();
  const score = (n: NetworkNode) =>
    (n.eu_id ? 2 : 0) + (n.register_court ? 1 : 0);
  for (const [, group] of byLabel) {
    if (group.length < 2) continue;
    const canonical =
      group.find((n) => n.id === data.eu_id) ??
      [...group].sort((a, b) => score(b) - score(a))[0];
    const merged: NetworkNode = { ...canonical };
    for (const n of group) {
      if (n.id === canonical.id) continue;
      remap.set(n.id, canonical.id);
      merged.eu_id = merged.eu_id ?? n.eu_id;
      merged.canonical_key = merged.canonical_key ?? n.canonical_key;
      merged.register_id = merged.register_id ?? n.register_id;
      merged.register_type = merged.register_type ?? n.register_type;
      merged.register_court = merged.register_court ?? n.register_court;
    }
    enriched.set(canonical.id, merged);
  }
  if (remap.size === 0) return data;
  const nodes = data.nodes
    .filter((n) => !remap.has(n.id))
    .map((n) => enriched.get(n.id) ?? n);
  const edgeByKey = new Map<string, NetworkEdge>();
  for (const e of data.edges) {
    const s = remap.get(e.source) ?? e.source;
    const t = remap.get(e.target) ?? e.target;
    if (s === t) continue; // self-loop created by the merge
    const key = `${s}->${t}|${e.role}`;
    const ne = { ...e, source: s, target: t };
    const prev = edgeByKey.get(key);
    if (!prev) edgeByKey.set(key, ne);
    else if (prev.share_percent == null && ne.share_percent != null)
      edgeByKey.set(key, ne);
  }
  return { ...data, nodes, edges: [...edgeByKey.values()] };
}

// Map a (German) backend role string to a stable i18n slug. Substring-matched,
// most-specific first (e.g. "Persönlich haftende(r) Gesellschafter(in)" must
// resolve to generalPartner, not shareholder). Gendered forms like "(in)" are
// handled automatically since we match on the stem.
export const ROLE_SLUGS: [RegExp, string][] = [
  [/persönlich haftend|persoenlich haftend|komplement/i, 'generalPartner'],
  [/kommanditist/i, 'limitedPartner'],
  [/aktion[äa]r|anteilseigner|gesellschafter/i, 'shareholder'],
  [/inhaber/i, 'owner'],
  [/vorstandsvorsitzend/i, 'chairman'],
  [/notvorstand/i, 'emergencyBoardMember'],
  [/vorstand/i, 'managementBoard'],
  [/gesch[äa]ftsf[üu]hr|notgesch[äa]ftsf[üu]hr/i, 'managingDirector'],
  [/prokur/i, 'authorizedSignatory'],
  [/aufsichtsrat/i, 'supervisoryBoard'],
  [/verwaltungsrat/i, 'administrativeBoard'],
  [/liquidator|abwickler/i, 'liquidator'],
  [/insolvenzverwalter/i, 'insolvencyAdministrator'],
  [/konkursverwalter/i, 'bankruptcyAdministrator'],
  [/treuh[äa]nder/i, 'trustee'],
  [/muttergesellschaft|konzernmutter|holding/i, 'parentCompany'],
  [/tochter/i, 'subsidiary'],
  [/zweigniederlassung/i, 'branch'],
  [/hauptniederlassung/i, 'headOffice'],
  [/gr[üu]nder/i, 'founder'],
  [/gesch[äa]ftsleiter/i, 'businessManager'],
  [/betriebsleiter/i, 'operationsManager'],
  [/werkleiter/i, 'plantManager'],
  [/direktor/i, 'director'],
  [/b[üu]rgermeister/i, 'mayor'],
  [/partner/i, 'partner'],
];
export function roleSlug(role: string): string | null {
  for (const [re, slug] of ROLE_SLUGS) if (re.test(role)) return slug;
  return null;
}
